"use client";

import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import * as React from "react";
import styled, { DefaultTheme } from "styled-components";

import { Spacer } from "../spacing";
import { themeOrDefault } from "../Theme";
import {
  HIGH_VIS_FILTER,
  highVisSlateComponent,
} from "../utils/highVisSlateComponent";

export interface IToggleProps {
  id?: string;
  /** The initial value of the toggle. */
  value: boolean;
  /** The callback to be run when the toggle is clicked. The value should be updated here. */
  onChange: (value: boolean) => void;
  /** By default, the text (On/Off) is shown to the right of the toggle. Use this to change that. */
  toggleTextToTheLeft?: boolean;
  /** When true, the on/off text will not be displayed. */
  hideText?: boolean;
  /** Prevents toggle from being clicked, and invokes the `disabled` styles. */
  disabled?: boolean;
  theme?: DefaultTheme;
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
  "data-test-id"?: string;
}

/**
 * @deprecated use `Toggle` from `@faire/slate/components/toggle` - since 2025-03-28
 *
 * @trackcomponent
 */
export const Toggle: React.FC<IToggleProps> = ({
  id,
  value,
  onChange,
  toggleTextToTheLeft,
  hideText,
  disabled,
  ...rest
}) => {
  const handleClicked = () => {
    if (disabled) {
      return;
    }
    onChange(!value);
  };

  return (
    <Container
      id={id}
      $reverseDirection={!!toggleTextToTheLeft}
      onClick={handleClicked}
      disabled={disabled}
      role="switch"
      aria-checked={value ? "true" : "false"}
      aria-labelledby={rest["aria-labelledby"]}
      aria-describedby={rest["aria-describedby"]}
      data-test-id={rest["data-test-id"]}
      type="button"
    >
      <ToggleWrapper $isOn={value} $disabled={disabled} theme={rest.theme}>
        <ToggleIndicator
          $isOn={value}
          $disabled={disabled}
          theme={rest.theme}
        />
      </ToggleWrapper>
      {hideText ? null : (
        <>
          <Spacer size={8} />
          <StyledTypography $isOn={value} $disabled={disabled}>
            {value ? (
              <StrictLocalMsg
                defaultMessage="On"
                description={{ text: "The switch is turned on" }}
              />
            ) : (
              <StrictLocalMsg
                defaultMessage="Off"
                description={{ text: "The switch is turned off" }}
              />
            )}
          </StyledTypography>
        </>
      )}
    </Container>
  );
};

const getBorderFromTheme = (
  isOn: boolean,
  disabled: boolean | undefined,
  theme: DefaultTheme
) => {
  const { toggle } = themeOrDefault(theme);
  return toggle[disabled ? "disabled" : "active"][isOn ? "on" : "off"].toggle
    .container.border;
};

const getContainerFillFromTheme = (
  isOn: boolean,
  disabled: boolean | undefined,
  theme: DefaultTheme
) => {
  const { toggle } = themeOrDefault(theme);
  return toggle[disabled ? "disabled" : "active"][isOn ? "on" : "off"].toggle
    .container.surface;
};

const getIndicatorFillFromTheme = (
  isOn: boolean,
  disabled: boolean | undefined,
  theme: DefaultTheme
) => {
  const { toggle } = themeOrDefault(theme);
  return toggle[disabled ? "disabled" : "active"][isOn ? "on" : "off"].toggle
    .indicator.surface;
};

const getColorFromTheme = (
  isOn: boolean,
  disabled: boolean | undefined,
  theme: DefaultTheme
) => {
  const { toggle } = themeOrDefault(theme);
  return toggle[disabled ? "disabled" : "active"][isOn ? "on" : "off"].label
    .text;
};

const Container = styled.button<{ $reverseDirection: boolean }>`
  display: flex;
  flex-direction: ${(props) =>
    props.$reverseDirection ? "row-reverse" : "row"};
  align-items: center;
  cursor: pointer;
  appearance: none;
  ${highVisSlateComponent(HIGH_VIS_FILTER.COMPONENT)}
`;

const ToggleWrapper = styled.div<{
  $isOn: boolean;
  $disabled: boolean | undefined;
}>`
  position: relative;

  width: 32px;
  border-radius: 100px;
  height: 18px;

  border: 1px solid
    ${(props) => getBorderFromTheme(props.$isOn, props.$disabled, props.theme)};
  background-color: ${(props) =>
    getContainerFillFromTheme(props.$isOn, props.$disabled, props.theme)};
`;

export const ToggleIndicator = styled.div<{
  $isOn: boolean;
  $disabled: boolean | undefined;
}>`
  position: absolute;
  background-color: ${(props) =>
    getIndicatorFillFromTheme(props.$isOn, props.$disabled, props.theme)};
  transition: left 0.1s linear;

  top: calc(50% - 4px);
  width: 8px;
  height: 8px;
  border-radius: 100px;
  left: ${(props) => (props.$isOn ? 18 : 4)}px;
`;

const StyledTypography = styled(Typography)<{
  $isOn: boolean;
  $disabled: boolean | undefined;
}>`
  &&& {
    color: ${(props) =>
      getColorFromTheme(props.$isOn, props.$disabled, props.theme)};
  }
`;
